import type {
  ICellRendererComp,
  ICellRendererParams,
  ColDef,
} from "ag-grid-enterprise";

interface SelectOption {
  id: number | string;
  name: string;
}

interface SelectConfig {
  value: number | string;
  options: SelectOption[];
  patch_url: string;
  // Omit both to PATCH a flat `{ [patch_value_key]: value }` body instead of the
  // collection-shaped `{ [patch_wrapper]: [{ id, [patch_value_key]: value }] }` one.
  patch_wrapper?: string;
  patch_id?: number | string;
  patch_value_key: string;
  // Leading "nothing picked yet" option; always submits an empty string.
  blank_option?: string;
}

// Dispatched (bubbling) after a successful PATCH so the owning component can react to the
// server's answer — e.g. toggling a "still incomplete" notice.
export const SELECT_CELL_SAVED_EVENT = "eb:select-cell-saved";

/**
 * StatusCellRenderer — generic presentation renderer.
 *
 * Handles three patterns, checked in order:
 *
 * 1. Select  — `field_select` key in row data (e.g. `role_select`)
 *    Renders an interactive <select>; on change PATCHes the configured endpoint.
 *    Config: { value, options, patch_url, patch_value_key, patch_wrapper?, patch_id?,
 *              blank_option? }
 *    Option ids may be numbers (org roles) or strings (voter-list column mapping); the id's own
 *    type is what gets sent back.
 *
 * 2. HTML    — `field_html` key in row data (e.g. `email_html`, `status_html`)
 *    Renders server-provided HTML with the `ag-status-cell` flex-column layout.
 *    Backwards-compatible: the existing `status` column uses `status_html` which
 *    resolves naturally via `field + "_html"` when field === "status".
 *
 * 3. Plain   — falls back to `params.value` as text.
 *
 * The `actionsCellRenderer` is the counterpart for all interactive button actions.
 */
export class StatusCellRenderer implements ICellRendererComp {
  private eGui!: HTMLElement;

  init(params: ICellRendererParams): void {
    this.eGui = document.createElement("div");

    if (params.node.group) {
      this.eGui.textContent = params.value ?? "";
      return;
    }

    const field = (params.colDef as ColDef)?.field ?? "";
    const data = params.data as Record<string, unknown>;

    // Pattern 1: Select
    const cfg = data[field + "_select"] as SelectConfig | undefined | null;
    if (cfg) {
      this.eGui.style.width = "100%";
      const select = document.createElement("select");
      select.style.width = "100%";
      select.style.maxWidth = "100%";

      if (cfg.blank_option) {
        const blank = document.createElement("option");
        blank.value = "";
        blank.textContent = cfg.blank_option;
        select.appendChild(blank);
      }

      cfg.options.forEach(({ id, name }) => {
        const opt = document.createElement("option");
        opt.value = String(id);
        opt.textContent = name;
        // String-compared so string-keyed configs match too; numeric ids are unaffected.
        if (String(id) === String(cfg.value)) opt.selected = true;
        select.appendChild(opt);
      });

      select.addEventListener("change", async () => {
        // Resolved back through the option so the id's own type survives select.value's string.
        const newOption = cfg.options.find(({ id }) => String(id) === select.value);
        const newValue: number | string = newOption ? newOption.id : "";
        const token =
          (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)
            ?.content ?? "";
        try {
          const response = await fetch(cfg.patch_url, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-CSRF-Token": token,
            },
            body: JSON.stringify(
              cfg.patch_wrapper
                ? {
                    [cfg.patch_wrapper]: [
                      { id: cfg.patch_id, [cfg.patch_value_key]: newValue },
                    ],
                  }
                : { [cfg.patch_value_key]: newValue }
            ),
          });
          const json = (await response.json()) as { status: string };
          if (json.status === "ok") {
            // Dispatched before setDataValue: that call refreshes the cell (refresh() always
            // returns false), detaching this element and killing the event's bubble path.
            this.eGui.dispatchEvent(
              new CustomEvent(SELECT_CELL_SAVED_EVENT, {
                bubbles: true,
                detail: { field, value: newValue, response: json },
              })
            );
            // Keep the grid's underlying row data (sort/filter/export source) in sync with
            // the dropdown — otherwise it keeps sorting/filtering on the pre-edit role name.
            // Update cfg.value before setDataValue — the latter triggers a synchronous
            // cell refresh, which reads cfg.value to pick the selected <option> when it
            // rebuilds the <select> from scratch.
            cfg.value = newValue;
            params.node.setDataValue(field, newOption ? newOption.name : "");
          } else {
            select.value = String(cfg.value);
          }
        } catch {
          select.value = String(cfg.value);
        }
      });

      this.eGui.appendChild(select);
      return;
    }

    // Pattern 2: HTML (ag-status-cell layout — only when HTML content present)
    const html = data[field + "_html"] as string | undefined;
    if (html) {
      this.eGui.className = "ag-status-cell";
      this.eGui.innerHTML = html.replace(/>\s+</g, "><").trim();
      this.delegateModalLinkClicks();
    } else {
      // Pattern 3: Plain text fallback
      this.eGui.textContent = params.value ?? "";
    }
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(): boolean {
    return false;
  }

  // Turbo Drive is off globally (data-turbo="true" opts a link in via its document-level click
  // listener) -- but this cell's markup is injected through innerHTML, outside the parse/connect
  // pass Turbo needs to ever see the link. Without this, an `a[data-turbo-frame="modal"]` injected
  // here (Audits::AdminMessagePresenter's spoil-ids link) would navigate the whole page instead of
  // opening the modal. Mirrors ActionsCellRenderer's own manual turbo-frame#modal handling.
  private delegateModalLinkClicks(): void {
    this.eGui.addEventListener("click", (e) => {
      const link = (e.target as HTMLElement)?.closest<HTMLAnchorElement>('a[data-turbo-frame="modal"]');
      if (!link) return;

      e.preventDefault();
      const frame = document.querySelector<HTMLElement>('turbo-frame#modal');
      if (frame) frame.setAttribute("src", link.href);
    });
  }
}
